import { ReactElement } from 'react'
import {
  SubHeader as Section,
  RankCircle,
  SectionTitle
} from '../../../components'
import { useHistory } from 'react-router-dom'
import { useSelector, shallowEqual } from 'react-redux'
import { RootState } from '../../../redux/store'
import {
  TestResultsHeader,
  HeaderImage,
  SectionWrapper,
  CardWrapper,
  UserWrapper,
  SectionContainer,
  ScoreWrapper,
  Icon,
  PerformanceWrapper,
  HighChartUI,
  TimeWrapper,
  TimeContainer,
  Separator
} from './subcomponents'
import strings from '../../../locale/en'
import { colors } from '../../../const/theme'
import { Container } from '../subcomponents'
import Figure from 'react-bootstrap/Figure'
import studentTeacherLogo from '../../../assets/student_teacher.png'
import { AnsweredTestResult, Question } from '../../../redux/test/type'
import * as Highcharts from 'highcharts'
import drilldown from 'highcharts/modules/drilldown'
import TestResults from './TestResults'
import determineOptions from './helpers'

drilldown(Highcharts)

const Results = (): ReactElement => {
  const { answeredTestResult, attemptedTests, selectedTestPaperId } =
    useSelector(
      (state: RootState) => ({
        answeredTestResult: state.test.answeredTestResult,
        attemptedTests: state.test.attemptedTests,
        selectedTestPaperId: state.test.selectedTestPaperId
      }),
      shallowEqual
    )
  const history = useHistory()

  const selectedTestPaper =
    attemptedTests.filter(
      (test) => test?.testPaperId === selectedTestPaperId
    )[0] || {}

  let questions: Array<Question> = []
  answeredTestResult?.sectionsData.forEach((section) => {
    questions.push(...section.sectionQuestion)
  })

  const currentRank = (
    <RankCircle
      rank={answeredTestResult?.currentRank || 0}
      themeKey="performanceCard"
    />
  )

  return (
    <Container id="container">
      <Section
        title={selectedTestPaper?.name}
        handleBack={() => {
          history.goBack()
        }}
      />
      <TestResultsHeader>
        <Section
          title={strings?.test?.currentRank}
          subTitle={`Out of ${answeredTestResult?.totalRank} students`}
          titleColor={colors.limeGreen}
          prefix={currentRank}
        />
        <HeaderImage>
          <Figure.Image alt="subject-logo" src={studentTeacherLogo} />
        </HeaderImage>
      </TestResultsHeader>
      <SectionContainer>
        <SectionWrapper>
          <SectionTitle title={strings.test.leaderBoard} />
          <CardWrapper>
            {answeredTestResult?.listTopRankers.map((ranker, index) => {
              return (
                <UserWrapper key={`user-${index}`}>
                  <Section
                    title={ranker?.studentName}
                    subTitle={`${ranker?.totalMarks}/${answeredTestResult?.totalMarks}`}
                    prefix={
                      <RankCircle
                        rank={ranker?.rank || 0}
                        width={50}
                        themeKey="leaderBoard"
                      />
                    }
                  />
                </UserWrapper>
              )
            })}
          </CardWrapper>
        </SectionWrapper>
        <SectionWrapper>
          <SectionTitle title={strings.test.score} />
          <CardWrapper>
            <Section
              title={strings.test.yourScore}
              subTitle={`Out of ${answeredTestResult?.totalMarks}`}
              prefix={
                <RankCircle
                  rank={answeredTestResult?.totalObtainedMarks || 0}
                  themeKey="leaderBoard"
                />
              }
            />
            <ScoreWrapper>
              <Section
                prefix={
                  <Icon
                    icon={['fas', 'check']}
                    size="1x"
                    color={colors.limeGreen}
                  />
                }
                title={strings.test.correct}
                subTitle={`${answeredTestResult?.totalCorrectAnsweredQuestion}`}
              />
              <Section
                prefix={
                  <Icon icon={['fas', 'times']} size="1x" color={colors.red} />
                }
                title={strings.test.inCorrect}
                subTitle={`${answeredTestResult?.totalWrongAttemptedQuestions}`}
              />
              <Section
                prefix={<Icon icon={['fas', 'exclamation']} size="1x" />}
                title={strings.test.unAnswered}
                subTitle={`${answeredTestResult?.totalUnAttemptedQuestons}`}
              />
              <Section
                prefix={
                  <Icon
                    icon={['fas', 'crosshairs']}
                    size="1x"
                    color={colors.gray}
                  />
                }
                title={strings.test.accuracy}
                subTitle={`${answeredTestResult?.accuracy}%`}
              />
            </ScoreWrapper>
          </CardWrapper>
        </SectionWrapper>
      </SectionContainer>
      <PerformanceWrapper>
        <SectionTitle title={strings.test.performance} />
        <CardWrapper>
          <HighChartUI
            containerProps={{ style: { width: '100%' } }}
            highcharts={Highcharts}
            options={determineOptions(
              answeredTestResult as AnsweredTestResult,
              questions
            )}
          />
        </CardWrapper>
      </PerformanceWrapper>
      <TimeContainer>
        <SectionWrapper>
          <TimeWrapper>
            <Section
              title={strings.test.totalTimeTaken}
              subTitle={`${
                answeredTestResult
                  ? (answeredTestResult.totalConsumeTime / 60).toFixed(2)
                  : 0
              } minutes`}
            />
            <Separator />
            <Section
              title={strings.test.averageTimeTaken}
              subTitle={`${
                answeredTestResult
                  ? (
                      answeredTestResult.totalConsumeTime /
                      answeredTestResult.totalQuestions
                    ).toFixed(2)
                  : 0
              } seconds`}
            />
          </TimeWrapper>
        </SectionWrapper>
        <SectionWrapper>
          <TimeWrapper>
            <Section
              title={strings.test.toppersTimeTaken}
              subTitle={`${
                answeredTestResult
                  ? (answeredTestResult.totalTimeTakenByTopper / 60).toFixed(2)
                  : 0
              } minutes`}
            />
            <Separator />
            <Section
              title={strings.test.averageTimeTaken}
              subTitle={`${
                answeredTestResult
                  ? (
                      answeredTestResult.totalTimeTakenByTopper /
                      answeredTestResult.totalQuestions
                    ).toFixed(2)
                  : 0
              } seconds`}
            />
          </TimeWrapper>
        </SectionWrapper>
      </TimeContainer>
      <TestResults questions={questions} />
    </Container>
  )
}

export default Results
